import { useCallback, useMemo } from 'react';
import { type RefinementCtx } from 'zod';

import {
  type CohortBuilderDefinitionLegacy,
  CohortDefinitionZodModel,
} from '@verantos-dev/types-cohorts';

import { populateIncorrectFilterOptionValues } from './eventFilters';
import { populateIds } from './populateIds';
import { transformer } from './transformer';
import { validations, ValidationCodes } from './validations';

/**
 * Hook for validating and transforming cohort definitions.
 *
 * FEATURE FLAG: The Zod schema includes optional `analyticalVariables` and `criteria` fields
 * that are gated by the `analytical_variables_in_cohort_builder` feature flag.
 * Runtime validation accepts these fields when present but does not require them,
 * allowing backward compatibility with old cohort definitions.
 *
 * CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag:
 * - Update Zod schema to make `analyticalVariables` and `criteria` required
 * - Remove this comment and feature flag documentation
 */
const useValidateCohortDefinition = () => {
  const CohortDefinitionValidateModel = useMemo(() => {
    return CohortDefinitionZodModel.superRefine(
      validations() as (arg: Record<string, unknown>, ctx: RefinementCtx) => void
    );
  }, []);

  const transformCohortDefinition = useCallback(
    (
      cohortDefinition: CohortBuilderDefinitionLegacy,
      options?: { addIds?: boolean; dirty?: boolean }
    ): CohortBuilderDefinitionLegacy => {
      const { addIds = false, dirty = false } = options || {};
      let _cohortDefinition = transformer(cohortDefinition);
      if (addIds) {
        _cohortDefinition = populateIds(_cohortDefinition, { dirty });
        _cohortDefinition = populateIncorrectFilterOptionValues(_cohortDefinition);
      }
      return _cohortDefinition;
    },
    []
  );

  const validateCohortDefinition = useCallback(
    (cohortDefinition: CohortBuilderDefinitionLegacy) => {
      const result = CohortDefinitionValidateModel.safeParse(cohortDefinition);
      return {
        data: result.data || cohortDefinition,
        errors: result.error,
        success: result.success,
      };
    },
    [CohortDefinitionValidateModel]
  );

  return {
    transformCohortDefinition,
    validateCohortDefinition,
  };
};

export { useValidateCohortDefinition, ValidationCodes };
